/**
 * Step 1 — sélection entreprise.
 *
 * Pré-remplissage `?company_id=...` (D-A6) géré au niveau parent :
 * la query string est lue dans ProjectIntakeMvpPage et injectée dans
 * `data.company_id` avant le mount de ce component.
 *
 * 9.T.4 post-smoke fix 3e itération — le picker de cet étape sélectionne
 * une compagnie EXISTANTE. On utilise `ExistingCompanyPicker` (ILIKE simple
 * sur legal_name) et non `CompanyPicker` 9.T.2 (trigram anti-doublon qui
 * échouait sur fragments — chercher "9444" ne trouvait pas "9444-1111 Qc
 * inc" faute d'atteindre le seuil trigram). Le chemin création est un lien
 * séparé explicite vers `/companies/new`, distinct du picker.
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ExistingCompanyPicker } from "../../../components/ExistingCompanyPicker";
import { construireDetour } from "../../../lib/retourParcours";
import type { IntakeMvpData } from "../../../lib/intakeDraftStorage";

type Props = {
  data: IntakeMvpData;
  onChange: (next: Partial<IntakeMvpData>) => void;
  /**
   * 9.BA.1 — l'intention du parcours, portée jusqu'au détour.
   *
   * Sans elle, créer une entreprise depuis une « nouvelle demande » ramènerait
   * dans un simple « nouveau projet » : le parcours s'arrêterait avant l'envoi,
   * et l'utilisateur ne saurait pas pourquoi.
   */
  intention: string | null;
};

export function Step1Company({ data, onChange, intention }: Props) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">
          {t("projects.intake.step1.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t("projects.intake.step1.subtitle")}</p>
      </div>
      <label htmlFor="intake-company" className="block">
        <span className="label">{t("projects.fields.company")} *</span>
        <ExistingCompanyPicker
          id="intake-company"
          value={data.company_id}
          onChange={(id) => onChange({ company_id: id, contact_id: undefined })}
        />
      </label>
      <p className="text-xs text-slate-600">
        {t("projects.intake.step1.create_new_hint")}{" "}
        <Link
          to={construireDetour({
            vers: "/companies/new",
            destination: "projet-nouveau",
            intention,
          })}
          className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 rounded font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2"
          data-testid="step1-create-company-link"
        >
          {t("projects.intake.step1.create_new_cta")}
        </Link>
      </p>
    </div>
  );
}
